import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Loader2, ShieldCheck, Lock, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

// Lazy-init the Stripe.js promise so we don't hit Stripe on every render
let stripePromise = null;
const getStripe = () => {
  if (!PUBLISHABLE_KEY) return null;
  if (!stripePromise) stripePromise = loadStripe(PUBLISHABLE_KEY);
  return stripePromise;
};

const APPEARANCE = {
  theme: "stripe",
  variables: {
    fontFamily: "'Manrope', -apple-system, system-ui, sans-serif",
    fontSizeBase: "15px",
    colorPrimary: "#DC2626",
    colorBackground: "#ffffff",
    colorText: "#0A1128",
    colorDanger: "#b91c1c",
    borderRadius: "0px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": { border: "1px solid #cbd5e1", padding: "12px 14px", boxShadow: "none" },
    ".Input:focus": { border: "1px solid #0A1128", boxShadow: "0 0 0 2px rgba(10,17,40,0.08)" },
    ".Label": { fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "11px", color: "#475569" },
    ".Tab": { border: "1px solid #cbd5e1", padding: "12px" },
    ".Tab--selected": { border: "1px solid #0A1128", color: "#0A1128" },
  },
};

function CheckoutForm({ booking, total, onPaying }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true); setError(null); onPaying?.(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/betaling/success?booking_id=${booking.id}&via=embedded`,
      },
    });
    if (error) {
      setError(error.message || "Noget gik galt — prøv igen");
      setSubmitting(false); onPaying?.(false);
    }
    // On success, Stripe redirects to return_url. No further action needed here.
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="embedded-checkout-form">
      <PaymentElement options={{ layout: "tabs", paymentMethodOrder: ["card", "mobilepay"] }} />
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="checkout-error">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="btn-primary w-full justify-center text-base py-4 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="checkout-pay-button"
      >
        {submitting ? <><Loader2 className="animate-spin" size={18} /> Behandler betaling...</> : <><Lock size={16} /> Betal {Number(total).toFixed(2)} kr</>}
      </button>
      <div className="flex items-center justify-center gap-2 text-xs text-brand-text2">
        <ShieldCheck size={14} className="text-emerald-700" />
        Sikret af Stripe — kortdata sendes krypteret direkte til Stripe. Vi gemmer aldrig dine kortoplysninger.
      </div>
    </form>
  );
}

export default function EmbeddedCheckoutPage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const idFromQuery = params.get("booking_id");
  const realBookingId = bookingId || idFromQuery;
  const nav = useNavigate();
  const [state, setState] = React.useState({ status: "loading", booking: null, clientSecret: null, error: null });
  const [paying, setPaying] = React.useState(false);

  React.useEffect(() => {
    if (!realBookingId) { setState({ status: "error", error: "Mangler booking-id i URL" }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data: bk } = await api.get(`/bookings/${realBookingId}`);
        if (cancelled) return;
        if (bk.payment_status === "paid") {
          // Already paid — go straight to success
          nav(`/betaling/success?booking_id=${realBookingId}&via=embedded&already_paid=1`);
          return;
        }
        const { data: pi } = await api.post("/payments/create-intent", { booking_id: realBookingId });
        if (cancelled) return;
        if (!pi.client_secret) throw new Error("Stripe svarede uden client_secret");
        setState({ status: "ready", booking: bk, clientSecret: pi.client_secret, total: pi.amount, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: e?.response?.data?.detail || e?.message || "Kunne ikke starte betaling" });
      }
    })();
    return () => { cancelled = true; };
  }, [realBookingId, nav]);

  // Fail-fast UI when publishable key is not configured (dev preview without Stripe)
  if (!PUBLISHABLE_KEY) {
    return (
      <div className="bg-brand-bg2 min-h-[80vh]" data-testid="checkout-no-key">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
          <div className="bg-white border border-brand p-10">
            <div className="kicker">Konfiguration mangler</div>
            <h1 className="font-display font-bold text-2xl text-brand-dark mt-3">Stripe publishable key ikke konfigureret</h1>
            <p className="text-brand-text2 mt-3">
              Tilføj <code className="bg-brand-bg2 px-1.5 py-0.5 font-mono text-xs">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> til <code className="bg-brand-bg2 px-1.5 py-0.5 font-mono text-xs">frontend/.env</code> for at aktivere on-page betaling.
            </p>
            <p className="text-brand-text2 mt-2 text-sm">Find din key i Stripe Dashboard → Developers → API keys (begynder med <code className="font-mono">pk_test_</code> eller <code className="font-mono">pk_live_</code>).</p>
            <button onClick={() => nav(-1)} className="btn-secondary mt-6"><ArrowLeft size={16} /> Tilbage</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-bg2 min-h-[80vh]" data-testid="embedded-checkout-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-12">
        <button onClick={() => !paying && nav(-1)} disabled={paying} className="text-brand-text2 hover:text-brand-dark text-sm flex items-center gap-1.5 mb-6 disabled:opacity-50" data-testid="checkout-back">
          <ArrowLeft size={14} /> Tilbage til ordre
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Stripe Elements form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-brand p-6 sm:p-8">
              <div className="kicker">Sikker betaling</div>
              <h1 className="font-display font-bold text-3xl text-brand-dark mt-2">Betal din booking</h1>
              <p className="text-brand-text2 text-sm mt-2">Kort, Apple Pay, Google Pay og MobilePay accepteres. Du forlader aldrig emakurer.dk.</p>

              <div className="mt-7">
                {state.status === "loading" && (
                  <div className="flex flex-col items-center py-12 text-brand-text2" data-testid="checkout-loading">
                    <Loader2 className="animate-spin text-brand-red" size={32} />
                    <div className="mt-3 text-sm">Forbereder betalingsformular...</div>
                  </div>
                )}
                {state.status === "error" && (
                  <div className="bg-red-50 border border-red-200 text-red-900 p-5" data-testid="checkout-init-error">
                    <div className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> Kunne ikke starte betaling</div>
                    <p className="text-sm mt-2">{state.error}</p>
                    <button onClick={() => nav(`/book`)} className="btn-secondary mt-4">Tilbage til booking</button>
                  </div>
                )}
                {state.status === "ready" && state.clientSecret && (
                  <Elements stripe={getStripe()} options={{ clientSecret: state.clientSecret, appearance: APPEARANCE, locale: "da" }}>
                    <CheckoutForm booking={state.booking} total={state.total} onPaying={setPaying} />
                  </Elements>
                )}
              </div>
            </div>
          </div>

          {/* Right: Order summary */}
          <div className="lg:col-span-5">
            <div className="bg-brand-dark text-white p-6 sm:p-8 sticky top-6">
              <div className="kicker text-brand-red">Din ordre</div>
              {state.booking ? (
                <>
                  <div className="font-display font-bold text-xl mt-2">Booking {state.booking.booking_number}</div>
                  <div className="text-white/70 text-sm mt-1">{state.booking.pickup_city || state.booking.pickup_address} → {state.booking.delivery_city || state.booking.delivery_address}</div>
                  <div className="space-y-2 mt-6 text-sm border-t border-white/10 pt-5">
                    <div className="flex justify-between"><span className="text-white/70">Service</span><span className="font-semibold">{({ same_day: "Same-Day", express: "Express", evening: "Aften", business: "Erhverv" })[state.booking.delivery_type] || state.booking.delivery_type}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Pakker</span><span className="font-semibold">{state.booking.package_count}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Vægt</span><span className="font-semibold">{state.booking.weight_kg} kg</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Distance</span><span className="font-semibold">{state.booking.distance_km} km</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Afhentning</span><span className="font-semibold">{state.booking.pickup_time}</span></div>
                  </div>
                  <div className="border-t border-white/10 mt-5 pt-5">
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/60">Total inkl. moms</div>
                        <div className="font-display font-bold text-3xl mt-1">{Number(state.total ?? state.booking.total_amount_dkk).toFixed(2)} kr</div>
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">25% moms</div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-white/60 text-sm py-6">Henter ordre...</div>
              )}
              <div className="border-t border-white/10 mt-5 pt-5 space-y-2 text-xs text-white/60">
                <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400" /> 256-bit SSL kryptering</div>
                <div className="flex items-center gap-2"><Lock size={12} className="text-emerald-400" /> PCI DSS Level 1 compliant</div>
                <div className="flex items-center gap-2"><img alt="" src="https://images.stripeassets.com/fzn2n1nzq965/HTTOloNPhisV9P4hlMPNA/cacf1bb88b9fc492dfad34378d844280/Stripe_wordmark_-_blurple.svg" className="h-3 invert opacity-60" /></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
